import { alpha, createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { defaultDarkTheme, defaultLightTheme } from 'react-admin';
import type { BrandTokens, ThemeMode } from '../../config/apps';

// MUI red 500. A status colour rather than a brand colour, so it is shared by
// every app instead of coming from the brand tokens.
const ERROR_BORDER = '#f44336';

/**
 * Builds one MUI theme for an app's brand tokens in the requested mode.
 *
 * Every accent inside the component overrides reads from the brand palette, so
 * a new app needs only a config entry — and both modes carry the app's brand.
 */
export const createAppTheme = (brand: BrandTokens, mode: ThemeMode): Theme => {
    const base = mode === 'light' ? defaultLightTheme : defaultDarkTheme;
    const palette = brand[mode];
    const headingFont = brand.fonts.heading;

    // Greys and shadows that depend on the mode rather than on the brand.
    const neutrals =
        mode === 'light'
            ? {
                  fieldsetBorder: '#e0e0e0',
                  // Light mode previews the brand on hover; dark mode brightens
                  // the neutral instead, where an accent tint reads as a focus ring.
                  fieldsetHoverBorder: palette.primary.main,
                  filledBackground: '#f5f5f5',
                  filledHoverBackground: '#eeeeee',
                  containedShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  containedHoverShadow: '0 4px 8px rgba(0,0,0,0.15)',
              }
            : {
                  fieldsetBorder: 'rgba(255, 255, 255, 0.23)',
                  fieldsetHoverBorder: 'rgba(255, 255, 255, 0.5)',
                  filledBackground: 'rgba(255, 255, 255, 0.09)',
                  filledHoverBackground: 'rgba(255, 255, 255, 0.13)',
                  containedShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  containedHoverShadow: '0 4px 8px rgba(0,0,0,0.4)',
              };

    return createTheme({
        ...base,
        palette: {
            ...base.palette,
            mode,
            primary: palette.primary,
            secondary: palette.secondary,
            background: {
                default: palette.background.default,
                paper: palette.background.paper,
            },
        },
        shape: { borderRadius: 8 },
        typography: {
            ...base.typography,
            htmlFontSize: 16,
            fontFamily: brand.fonts.body,
            h1: { fontFamily: headingFont, fontWeight: 400, fontSize: '2.5rem', letterSpacing: '-0.02em', lineHeight: 1.15 },
            h2: { fontFamily: headingFont, fontWeight: 400, fontSize: '2rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
            h3: { fontFamily: headingFont, fontWeight: 400, fontSize: '1.75rem', letterSpacing: '-0.01em', lineHeight: 1.25 },
            h4: { fontFamily: headingFont, fontWeight: 400, fontSize: '1.5rem', lineHeight: 1.3 },
            h5: { fontFamily: headingFont, fontWeight: 400, fontSize: '1.25rem', lineHeight: 1.35 },
            h6: { fontFamily: headingFont, fontWeight: 400, fontSize: '1.125rem', lineHeight: 1.4 },
            button: { fontSize: '0.9375rem', fontWeight: 500, textTransform: 'none' },
        },
        components: {
            ...base.components,
            MuiAppBar: {
                defaultProps: { elevation: 0, color: 'transparent' },
                styleOverrides: {
                    root: ({ theme: t }) => ({
                        backgroundColor: t.palette.background.paper,
                        backgroundImage: 'none',
                        color: t.palette.text.primary,
                        borderBottom: `1px solid ${alpha(t.palette.primary.main, 0.12)}`,
                        boxShadow: `0 1px 3px ${alpha(t.palette.common.black, 0.04)}`,
                    }),
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined' as const,
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        '& fieldset': {
                            borderColor: neutrals.fieldsetBorder,
                            transition: 'all 0.3s ease',
                        },
                        '&:hover fieldset': {
                            borderColor: neutrals.fieldsetHoverBorder,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: palette.primary.main,
                            borderWidth: '2px',
                        },
                        '&.Mui-error fieldset': {
                            borderColor: ERROR_BORDER,
                        },
                        // Reset padding for multiline inputs to allow proper inner control
                        '& .MuiInputBase-inputMultiline': {
                            padding: '8px 12px',
                        },
                    },
                    input: {
                        padding: '12px 14px',
                        fontSize: '14px',
                    },
                    multiline: {
                        padding: '0', // Reset to allow specific multiline input padding
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '14px',
                        '&.Mui-focused': {
                            color: palette.primary.main,
                        },
                    },
                    outlined: {
                        transform: 'translate(14px, 14px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -9px) scale(0.85)',
                            backgroundColor: palette.background.paper,
                            padding: '0 4px',
                        },
                    },
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px 8px 0 0',
                        backgroundColor: neutrals.filledBackground,
                        '&:before': {
                            display: 'none',
                        },
                        '&:after': {
                            borderBottom: `2px solid ${palette.primary.main}`,
                        },
                        '&:hover': {
                            backgroundColor: neutrals.filledHoverBackground,
                        },
                        '&.Mui-focused': {
                            backgroundColor: neutrals.filledBackground,
                        },
                    },
                    input: {
                        padding: '12px 14px',
                        paddingTop: '20px',
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            padding: '4px 9px',
                        },
                    },
                    inputRoot: {
                        padding: '2px 4px',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: '8px 16px',
                        transition: 'all 0.2s ease',
                    },
                    contained: {
                        boxShadow: neutrals.containedShadow,
                        '&:hover': {
                            boxShadow: neutrals.containedHoverShadow,
                        },
                    },
                },
            },
            // Render React Admin's <CreateButton> as a primary contained button to
            // match sirk-asset-frontend's "+ Opprett objekt" CTA. Covers every
            // "+ Opprett" / "+ Inviter" call-site without per-page changes.
            RaCreateButton: {
                defaultProps: {
                    variant: 'contained',
                    color: 'primary',
                },
            },
        },
    });
};
